import type { ReactNode } from "react";
import { CircleAlert, Info, TriangleAlert } from "lucide-react";
import { cx } from "./cx";

export type CalloutTone = "neutral" | "attention" | "waiting" | "info";

const TONES: Record<CalloutTone, { box: string; icon: string }> = {
  neutral: { box: "bg-surface-2", icon: "text-muted" },
  attention: { box: "bg-attention-soft", icon: "text-attention" },
  waiting: { box: "bg-waiting-soft", icon: "text-waiting" },
  info: { box: "bg-accent-soft", icon: "text-link" },
};

const ICONS: Record<CalloutTone, typeof Info> = {
  neutral: Info,
  attention: CircleAlert,
  waiting: TriangleAlert,
  info: Info,
};

export function Callout({
  tone = "neutral",
  title,
  children,
  actions,
  role,
  className,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  role?: "alert" | "status";
  className?: string;
}) {
  const Icon = ICONS[tone];
  return (
    <div
      role={role}
      className={cx("flex items-start gap-[9px] rounded-xl px-3 py-2.5 text-ui text-fg", TONES[tone].box, className)}
    >
      <Icon size={14} strokeWidth={1.8} aria-hidden="true" className={cx("mt-0.5", TONES[tone].icon)} />
      <div className="min-w-0 flex-1">
        {title ? <b className="font-semibold">{title}</b> : null}
        {children ? <div className={cx("text-muted", title ? "mt-0.5" : undefined)}>{children}</div> : null}
        {actions ? <div className="mt-2 flex flex-wrap gap-1.5">{actions}</div> : null}
      </div>
    </div>
  );
}
